import LoginSignUpSwitch from "@/components/LoginSignUpSwitch";
import { ErrorMessage, Input } from "@/components/UI";
import PhoneInput from "@/components/UI/PhoneInput";
import { NewUserData } from "@/data";
import { User } from "@/data/interfaces/User";
import ROUTES from "@/data/Routes";
import { Button } from "@/components/UI/Buttons/Button";
import { ArrowRight } from "lucide-react";
import { FC, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import Container from "../Container";
import { Separator } from "../UI/Separator";
import GoogleAuthButton from "../Auth/GoogleAuthButton";
import { cn } from "@/lib/utils";
import { useFormik } from "formik";
import { toFormikValidate } from "zod-formik-adapter";
import { z } from "zod";
import { REQUIRED_FIELDS_ERROR, isPhoneValid } from "@/utils/formValidator";
import { getProfileNameParts } from "@/utils/isUserInfoFilled";

const phoneCountryCheck = (data: { phone: string; phoneCountry?: string }) =>
  isPhoneValid(data.phone, data.phoneCountry);
const phoneCountryError = {
  message: "Telefono numeris neatitinka pasirinktos šalies",
  path: ["phone"],
};

const baseSchemaFields = z.object({
  name: z.string().min(1, "Vardas yra privalomas"),
  surname: z.string().min(1, "Pavardė yra privaloma"),
  email: z.string().email("El. pašto formatas netinkamas"),
  phone: z
    .string()
    .min(1, "Telefono numeris yra privalomas")
    .refine((v) => isPhoneValid(v), "Neteisingas telefono numerio formatas"),
  phoneCountry: z.string().optional(),
});

const baseSchema = baseSchemaFields.refine(phoneCountryCheck, phoneCountryError);

const creationSchema = baseSchemaFields
  .extend({
    password: z
      .string()
      .min(8, "Slaptažodis turi turėti bent 8 simbolius")
      .regex(
        /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).*$/,
        "Slaptažodis turi turėti bent 1 didžiąją, 1 mažają raidę ir skaičių",
      ),
    password_confirmation: z.string().min(8),
  })
  .refine((data) => data.password === data.password_confirmation, {
    message: "Slaptažodžiai nesutampa",
    path: ["password_confirmation"],
  })
  .refine(phoneCountryCheck, phoneCountryError);

const AccountCreationForm: FC<{
  onSubmit: (data: NewUserData) => Promise<void>;
  isUpdate?: boolean;
  className?: string;
  user?: User;
  /** Prefill from order registration API or similar (reinitializes the form when set). */
  externalPrefill?: Partial<
    Pick<NewUserData, "name" | "surname" | "email" | "phone">
  > | null;
  /** Shown when registering after checkout (city is not part of the register payload). */
  orderCityHint?: string;
}> = ({ onSubmit, isUpdate = false, className, user, externalPrefill }) => {
  const { state } = useLocation();
  const prefilledState = (state ?? {}) as Partial<NewUserData>;
  const [formError, setFormError] = useState<string>();
  const [isRequestInProgress, setIsRequestInProgress] = useState(false);

  const profileFromUser = isUpdate && user ? getProfileNameParts(user) : null;

  const initialValues = {
    name:
      profileFromUser?.firstName ??
      user?.name ??
      prefilledState.name ??
      externalPrefill?.name ??
      "",
    surname:
      profileFromUser?.surname ??
      user?.surname ??
      prefilledState.surname ??
      externalPrefill?.surname ??
      "",
    email: user?.email ?? prefilledState.email ?? externalPrefill?.email ?? "",
    phone: user?.phone ?? prefilledState.phone ?? externalPrefill?.phone ?? "",
    phoneCountry: "LT",
    password: "",
    password_confirmation: "",
  };

  const schema = isUpdate ? baseSchema : creationSchema;

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validate: toFormikValidate(schema),
    onSubmit: async (values) => {
      setFormError(undefined);
      setIsRequestInProgress(true);
      try {
        await onSubmit(values);
      } catch (error: any) {
        setFormError(error.message || "Klaida registruojantis");
      } finally {
        setIsRequestInProgress(false);
      }
    },
  });

  const isPrefilledFromUser = (field: "surname" | "phone"): boolean => {
    if (!isUpdate || !user) return false;
    return !!user[field]?.trim();
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const values = formik.values;
    const isEmpty =
      !values.name.trim() &&
      !values.surname.trim() &&
      !values.email.trim() &&
      !values.phone.trim() &&
      !isUpdate &&
      !values.password.trim() &&
      !values.password_confirmation.trim();
    const errors = await formik.validateForm();
    if (isEmpty) {
      setFormError(REQUIRED_FIELDS_ERROR);
      return;
    }
    if (Object.keys(errors).length) {
      setFormError(Object.values(errors)[0] ?? REQUIRED_FIELDS_ERROR);
      return;
    }
    await formik.submitForm();
  };

  const form = (
    <div className="text-grey mx-auto flex w-full max-w-[860px] flex-col">
      {!isUpdate && (
        <div className="mb-9 flex w-full justify-center">
          <LoginSignUpSwitch variant="plain" />
        </div>
      )}

      <form
        className="flex w-full flex-col gap-7.5"
        onSubmit={handleSubmit}
        noValidate
      >
        <ErrorMessage error={formError} />
        <div className="grid w-full gap-[18px] md:grid-cols-2">
          <Input
            label="Vardas*"
            type="text"
            name="name"
            value={formik.values.name}
            required
            autoComplete="given-name"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={isUpdate}
          />
          <Input
            label="Pavardė*"
            type="text"
            name="surname"
            value={formik.values.surname}
            required
            autoComplete="family-name"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={isPrefilledFromUser("surname")}
          />
          <Input
            label="El. paštas*"
            type="email"
            className="md:col-span-2"
            name="email"
            value={formik.values.email}
            required
            autoComplete="email"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={isUpdate}
          />
          {!isUpdate && (
            <>
              <Input
                label="Slaptažodis*"
                type="password"
                name="password"
                value={formik.values.password}
                required
                minLength={8}
                autoComplete="new-password"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              <Input
                label="Pakartoti slaptažodį*"
                type="password"
                name="password_confirmation"
                value={formik.values.password_confirmation}
                required
                minLength={8}
                autoComplete="new-password"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </>
          )}
          <PhoneInput
            label="Telefono nr.*"
            id="phone"
            name="phone"
            className="md:col-span-2"
            value={formik.values.phone}
            onChange={(val) => formik.setFieldValue("phone", val)}
            onCountryChange={(c) => formik.setFieldValue("phoneCountry", c ?? "LT")}
            onBlur={formik.handleBlur}
            disabled={isPrefilledFromUser("phone")}
            error={
              formik.touched.phone && formik.errors.phone
                ? String(formik.errors.phone)
                : undefined
            }
          />
          {!isUpdate && (
            <div className="text-muted space-y-2 text-sm leading-6 md:col-span-2">
              <p>
                Kurdami paskyrą, sutinkate su mūsų{" "}
                <NavLink
                  to={ROUTES.COOKIES_POLICY}
                  className="hover:text-ink underline underline-offset-4 transition-colors"
                >
                  naudojimo sąlygomis
                </NavLink>
                .
              </p>
              <p>
                Sužinokite, kaip tvarkome jūsų duomenis, mūsų{" "}
                <NavLink
                  to={ROUTES.PRIVACY_POLICY}
                  className="hover:text-ink underline underline-offset-4 transition-colors"
                >
                  privatumo pranešime
                </NavLink>
                .
              </p>
            </div>
          )}
        </div>
        <Button
          variant="ink"
          size="default"
          disabled={isRequestInProgress}
          type="submit"
          className={cn(
            "relative mx-auto h-[58px] w-full justify-between rounded-full text-[18px] font-normal",
            isUpdate ? "max-w-[420px]" : "max-w-[330px]",
          )}
          onHover
          hoverGradientLight
        >
          {isUpdate ? "Atnaujinti" : "Registruotis"}
          <ArrowRight className="absolute size-5 opacity-85" />
        </Button>

        {!isUpdate && (
          <>
            <Separator label="arba" labelClassName="my-1 text-muted" />
            <GoogleAuthButton buttonClassName="mx-auto h-[58px] w-full max-w-[330px] rounded-full text-[18px] font-normal md:h-[58px]" />
          </>
        )}
      </form>
    </div>
  );

  if (isUpdate) {
    return (
      <Container className={cn("min-h-screen px-4 md:w-[600px]", className)}>
        {form}
      </Container>
    );
  }

  return (
    <section className="bg-canvas px-4 pb-14 md:px-6 md:pb-20">
      <Container className="pt-25 md:pt-50">
        <div className={cn("mx-auto w-full max-w-[600px]", className)}>
          {form}
        </div>
      </Container>
    </section>
  );
};

export default AccountCreationForm;
